import '../App.css';

import { zodResolver } from "@hookform/resolvers/zod";
import { UpdateIcon } from '@radix-ui/react-icons';
import axios from 'axios';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from "zod";
import PasswordValidation from '../components/passwordValidation';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel, FormMessage
} from '../components/ui/form';
import { Input } from '../components/ui/input';
import { toast } from '../components/ui/use-toast';

const signupSchema = z.object({
  password: z.string({
    required_error: "Nytt lösenord krävs."
  })
    .min(8, "Lösenordet måste vara mellan 8 och 20 tecken långt.")
    .max(20, "Lösenordet måste vara mellan 8 och 20 tecken långt.")
    .regex(/(?=.*[0-9])/, "Lösenordet måste innehålla minst en siffra (0-9).")
    .regex(/(?=.*[a-zåäö])/, "Lösenordet måste innehålla minst en liten bokstav (a-ö).")
    .regex(/(?=.*[A-ZÅÄÖ])/, "Lösenordet måste innehålla minst en stor bokstav (A-Ö).")
    .regex(/(?=.*[@!.#$%^&-+=()])/, "Lösenordet måste innehålla minst ett specialtecken (@!.#$%^&-+=()).")
    .refine(password => !password.includes(" "), {
      message: "Lösenord får inte innehålla mellanslag."
    }),
  password_confirmation: z.string({
    required_error: "Bekräfta lösenord krävs."
  }),
}).refine(data => data.password === data.password_confirmation, {
  message: "Lösenorden matchar inte.",
  path: ["password_confirmation"],
});

function Password() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      password: "",
      password_confirmation: "",
    }
  });

  async function onSubmit(values) {
    if (isLoading) return;

    setIsLoading(true);

    // Get query parameter resetToken from URL
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('resetToken');

    axios.post('https://skhrf-api.cmdigital.se/v1/auth/reset-password', {
      ...values,
      resetToken: resetToken
    }).then((response) => {
      if (response.status === 200) {
        toast({
          variant: 'success',
          title: 'Återställning av lösenord lyckad!',
          description: 'Använd de nya inloggningsuppgifterna för att logga in.'
        })
        navigate('/login');
      }
      setIsLoading(false);
    }).catch((error) => {
      setIsLoading(false);
      if (error?.response?.data?.error === "Invalid reset token") {
        toast({
          variant: 'destructive',
          title: 'Något gick fel.',
          description: "Din återställningslänk är ogiltig.",
        });
      } else if (Array.isArray(error?.response?.data?.error)) {
        toast({
          variant: 'destructive',
          title: 'Något gick fel.',
          description: error?.response?.data?.error,
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Något gick fel.',
          description: "Ett oväntat fel har uppstått, försök igen senare.",
        });
      }
    })

  }

  return (
    <div className='flex w-full h-screen items-center justify-center'>
      <Card className='w-3/4 lg:w-7/12 shadow-md'>
        <CardHeader>
          <CardTitle>Återställ lösenord</CardTitle>
          <CardDescription>Ange ett nytt lösenord. Lösenordet måste:
          </CardDescription>
        </CardHeader>
        <CardContent className='grid gap-4'>
          <Form {...form} >
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col w-full gap-6">
              <div className='grid md:grid-cols-2 gap-3 items-start'>
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className='space-y-0 grid gap-2'>
                      <FormLabel htmlFor='password'>Nytt lösenord</FormLabel>
                      <FormControl>
                        <Input id='password' type='password' autoComplete='new-password' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password_confirmation"
                  render={({ field }) => (
                    <FormItem className='space-y-0 grid gap-2'>
                      <FormLabel htmlFor='password_confirmation'>Bekräfta lösenord</FormLabel>
                      <FormControl>
                        <Input id='password_confirmation' type='password' autoComplete='new-password' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? <UpdateIcon className='loader-spinner' /> : <>Återställ lösenord</>}
              </Button>
              <div>
                <PasswordValidation password={form.watch('password')} />
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}

export default Password;
